/**
 * usePermissions — RBAC-Powered Permission Hook
 *
 * Phase 1: Uses buildChecker from RBAC engine (role inheritance)
 * Phase 3: Will add per-user grants and workspace-level overrides
 *
 * The `can` function is memoized per user session — only recomputes
 * when the user's role or admin status changes.
 *
 * Usage:
 *   const { can, role, isAdmin } = usePermissions()
 *   if (can('cases.view.all')) { ... }
 *   if (can('inventory.delete')) { ... }
 */
import { useMemo, useCallback } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { buildChecker, type Permission } from '@/lib/rbac'

export interface UsePermissionsReturn {
  /** Check a single permission */
  can:     (permission: Permission) => boolean
  /** Check multiple permissions — returns true if ALL are granted */
  canAll:  (...permissions: Permission[]) => boolean
  /** Check multiple permissions — returns true if ANY is granted */
  canAny:  (...permissions: Permission[]) => boolean
  role:    string
  isAdmin: boolean
}

export function usePermissions(): UsePermissionsReturn {
  const { profile, isAdmin } = useAuth()

  // Do not let a profile flag or elevated-looking role label bypass the verified
  // administrator allowlist. Role permissions remain available through RBAC.
  const checker = useMemo(
    () => buildChecker(profile?.role ?? 'employee', isAdmin),
    [profile?.role, isAdmin]
  )

  const can    = useCallback((p: Permission) => checker(p), [checker])
  const canAll = useCallback((...ps: Permission[]) => ps.every(checker), [checker])
  const canAny = useCallback((...ps: Permission[]) => ps.some(checker), [checker])

  return {
    can,
    canAll,
    canAny,
    role:    profile?.role ?? 'employee',
    isAdmin,
  }
}
